import React, { memo, useCallback } from 'react';

import PropTypes from 'prop-types';
import { InputControlLabel } from '@picsio/ui';

import localization from '../../../shared/strings';
import DetailsBlock from '../../DetailsBlock';
import Tooltip from '../../Tooltip/Tooltip';
import ReadOnlyFieldTooltip from '../helpers/ReadOnlyFieldTooltip';
import { productFieldInputProps } from '../constants';
import buildComplianceFieldLabel from '../helpers/buildComplianceFieldLabel';
import {
  normalizeCountryCodeOfOrigin,
  validateCountryCodeOfOrigin,
  normalizeHarmonizedSystemCode,
  validateHarmonizedSystemCode,
} from '../helpers/validateComplianceFields';
import InventoryTextField from './InventoryTextField';
import Levels from './Levels';
import s from '../styles.module.scss';

const l10n = localization.PRODUCT_INFO.inventory;

const validateCountryField = (value) => validateCountryCodeOfOrigin(value, {
  invalidMessage: l10n.countryCodeOfOriginInvalid,
});
const validateHarmonizedSystemField = (value) => validateHarmonizedSystemCode(value, {
  invalidMessage: l10n.harmonizedSystemCodeInvalid,
});

const Inventory = ({
  tracked,
  sku,
  barcode,
  continueSelling,
  inventoryLevels,
  diff,
  detailsPanelVisibility,
  toggleVisibility,
  blockName,
  blockTitle,
  taxCode,
  countryCodeOfOrigin,
  harmonizedSystemCode,
  availableForSale,
  hideSku,
  hideBarcode,
  hideCompliance,
  hideTaxCode,
  hideInventoryPolicy,
  hideTracked,
  skuLabel,
  readOnly,
  readOnlyTooltip,
  onChangeSku,
  onChangeBarcode,
  onChangeTracked,
  onChangeInventoryPolicy,
  onChangeTaxCode,
  onChangeCountryCodeOfOrigin,
  onChangeHarmonizedSystemCode,
  onChangeAvailableForSale,
}) => {
  const saveTracked = useCallback(() => {
    if (readOnly) return;
    onChangeTracked(!tracked);
  }, [tracked, onChangeTracked, readOnly]);

  const saveInventoryPolicy = useCallback(() => {
    if (readOnly) return;
    onChangeInventoryPolicy(continueSelling ? 'DENY' : 'CONTINUE');
  }, [continueSelling, onChangeInventoryPolicy, readOnly]);

  const saveAvailableForSale = useCallback(() => {
    if (readOnly || !onChangeAvailableForSale) return;
    onChangeAvailableForSale(!availableForSale);
  }, [availableForSale, onChangeAvailableForSale, readOnly]);

  return (
    <DetailsBlock
      dataQa="productInventory"
      detailsPanelVisibility={detailsPanelVisibility}
      toggleVisibility={toggleVisibility}
      blockName={blockName}
      blockTitle={blockTitle}
    >
      <If condition={!hideSku || !hideBarcode}>
        <div className={s.row}>
          <If condition={!hideSku}>
            <InventoryTextField
              value={sku || ''}
              label={<Tooltip content={diff?.sku ? 'Unpublished changes' : null} placement="top"><span className={diff?.sku ? s.diffDot : undefined}>{skuLabel}</span></Tooltip>}
              readOnly={readOnly}
              readOnlyTooltip={readOnlyTooltip}
              inputProps={productFieldInputProps}
              onCommit={onChangeSku}
            />
          </If>
          <If condition={!hideBarcode}>
            <InventoryTextField
              value={barcode || ''}
              label={<Tooltip content={diff?.barcode ? 'Unpublished changes' : null} placement="top"><span className={diff?.barcode ? s.diffDot : undefined}>Barcode (ISBN, UPC, GTIN, etc.)</span></Tooltip>}
              readOnly={readOnly}
              readOnlyTooltip={readOnlyTooltip}
              inputProps={productFieldInputProps}
              onCommit={onChangeBarcode}
            />
          </If>
        </div>
        <hr className={s.hr} />
      </If>
      <If condition={!hideTracked}>
        <ReadOnlyFieldTooltip tooltip={readOnly ? readOnlyTooltip : null}>
          <div className={s.row}>
            <InputControlLabel
              control="checkbox"
              label={<Tooltip content={diff?.tracked ? 'Unpublished changes' : null} placement="top"><span className={diff?.tracked ? s.diffDot : undefined}>Track quantity</span></Tooltip>}
              value={tracked}
              disabled={readOnly}
              onChange={saveTracked}
            />
          </div>
        </ReadOnlyFieldTooltip>
        <If condition={tracked && !hideInventoryPolicy}>
          <ReadOnlyFieldTooltip tooltip={readOnly ? readOnlyTooltip : null}>
            <div className={s.row}>
              <InputControlLabel
                control="checkbox"
                label={<Tooltip content={diff?.inventoryPolicy ? 'Unpublished changes' : null} placement="top"><span className={diff?.inventoryPolicy ? s.diffDot : undefined}>Continue selling when out of stock</span></Tooltip>}
                value={continueSelling}
                disabled={readOnly}
                onChange={saveInventoryPolicy}
              />
            </div>
          </ReadOnlyFieldTooltip>
        </If>
        <If condition={tracked && inventoryLevels}>
          <Levels data={inventoryLevels} />
        </If>
      </If>
      <If condition={onChangeAvailableForSale}>
        <hr className={s.hr} />
        <ReadOnlyFieldTooltip tooltip={readOnly ? readOnlyTooltip : null}>
          <div className={s.row}>
            <InputControlLabel
              control="checkbox"
              label={(
                <Tooltip content={diff?.availableForSale ? 'Unpublished changes' : null} placement="top">
                  <span className={diff?.availableForSale ? s.diffDot : undefined}>{l10n.availableForSale}</span>
                </Tooltip>
              )}
              value={availableForSale}
              disabled={readOnly}
              onChange={saveAvailableForSale}
            />
          </div>
        </ReadOnlyFieldTooltip>
      </If>
      <If condition={!hideTaxCode}>
        <hr className={s.hr} />
        <div className={s.row}>
          <InventoryTextField
            value={taxCode || ''}
            label={(
              <Tooltip content={diff?.taxCode ? 'Unpublished changes' : null} placement="top">
                <span className={diff?.taxCode ? s.diffDot : undefined}>{l10n.taxCode}</span>
              </Tooltip>
            )}
            readOnly={readOnly}
            readOnlyTooltip={readOnlyTooltip}
            onCommit={onChangeTaxCode}
          />
        </div>
      </If>
      <If condition={!hideCompliance}>
        <hr className={s.hr} />
        <div className={s.row}>
          <InventoryTextField
            value={countryCodeOfOrigin || ''}
            label={buildComplianceFieldLabel({
              label: l10n.countryCodeOfOrigin,
              diff: diff?.countryCodeOfOrigin,
              infoTooltip: l10n.countryCodeOfOriginTooltip,
            })}
            placeholder={l10n.countryCodeOfOriginPlaceholder}
            normalize={normalizeCountryCodeOfOrigin}
            validate={validateCountryField}
            readOnly={readOnly}
            readOnlyTooltip={readOnlyTooltip}
            onCommit={onChangeCountryCodeOfOrigin}
          />
          <InventoryTextField
            value={harmonizedSystemCode || ''}
            label={buildComplianceFieldLabel({
              label: l10n.harmonizedSystemCode,
              diff: diff?.harmonizedSystemCode,
              infoTooltip: l10n.harmonizedSystemCodeTooltip,
            })}
            placeholder={l10n.harmonizedSystemCodePlaceholder}
            normalize={normalizeHarmonizedSystemCode}
            validate={validateHarmonizedSystemField}
            readOnly={readOnly}
            readOnlyTooltip={readOnlyTooltip}
            onCommit={onChangeHarmonizedSystemCode}
          />
        </div>
      </If>
    </DetailsBlock>
  );
};

Inventory.propTypes = {
  tracked: PropTypes.bool,
  sku: PropTypes.string,
  barcode: PropTypes.string,
  continueSelling: PropTypes.bool,
  availableForSale: PropTypes.bool,
  inventoryLevels: PropTypes.arrayOf(PropTypes.shape({})),
  taxCode: PropTypes.string,
  countryCodeOfOrigin: PropTypes.string,
  harmonizedSystemCode: PropTypes.string,
  diff: PropTypes.shape({
    sku: PropTypes.bool,
    barcode: PropTypes.bool,
    tracked: PropTypes.bool,
    inventoryPolicy: PropTypes.bool,
    availableForSale: PropTypes.bool,
    taxCode: PropTypes.bool,
    countryCodeOfOrigin: PropTypes.bool,
    harmonizedSystemCode: PropTypes.bool,
  }),
  detailsPanelVisibility: PropTypes.objectOf(PropTypes.bool).isRequired,
  toggleVisibility: PropTypes.func.isRequired,
  blockName: PropTypes.string.isRequired,
  blockTitle: PropTypes.node.isRequired,
  hideSku: PropTypes.bool,
  hideBarcode: PropTypes.bool,
  hideCompliance: PropTypes.bool,
  hideTaxCode: PropTypes.bool,
  hideInventoryPolicy: PropTypes.bool,
  hideTracked: PropTypes.bool,
  skuLabel: PropTypes.node,
  readOnly: PropTypes.bool,
  readOnlyTooltip: PropTypes.string,
  onChangeTaxCode: PropTypes.func,
  onChangeCountryCodeOfOrigin: PropTypes.func,
  onChangeHarmonizedSystemCode: PropTypes.func,
  onChangeSku: PropTypes.func.isRequired,
  onChangeBarcode: PropTypes.func.isRequired,
  onChangeTracked: PropTypes.func.isRequired,
  onChangeInventoryPolicy: PropTypes.func.isRequired,
  onChangeAvailableForSale: PropTypes.func,
};

Inventory.defaultProps = {
  tracked: false,
  sku: '',
  barcode: '',
  continueSelling: false,
  availableForSale: false,
  inventoryLevels: null,
  taxCode: '',
  countryCodeOfOrigin: '',
  harmonizedSystemCode: '',
  diff: {},
  hideSku: false,
  hideBarcode: false,
  hideCompliance: false,
  hideTaxCode: false,
  hideInventoryPolicy: false,
  hideTracked: false,
  skuLabel: 'SKU (Stock Keeping Unit)',
  readOnly: false,
  readOnlyTooltip: null,
  onChangeTaxCode: () => null,
  onChangeCountryCodeOfOrigin: () => null,
  onChangeHarmonizedSystemCode: () => null,
  onChangeAvailableForSale: null,
};

export default memo(Inventory);
